import React, { useState, useEffect, useRef } from 'react';
import { GoogleMap, useJsApiLoader, StandaloneSearchBox } from '@react-google-maps/api';
import { useFormik } from 'formik';
import DashboardLayout from './DashboardLayout';
import { updateCorporateProfileSignUpSchema } from '../../../schemas/corporateProfileUpdateSchema';
import { useDispatch, useSelector } from 'react-redux';
import { UpdateCorporateProfileAPI } from '../../../redux/slices/UpdateCorporateProfileSlice';
import { base, errorHandler } from '../../../constants/base';
import { toast } from 'react-toastify';
import { updateUser } from '../../../redux/slices/authSlice';

const MyProfile = () => {

    const dispatch = useDispatch();
    const loading = useSelector(state => state.UpdateCorporateProfileSlice.loading)
    const corporateUserData = useSelector((state) => state?.AuthSlice?.user);
    const [isEditing, setIsEditing] = useState(false);
    const corporateLogoImage = corporateUserData?.images[0]?.path;
    const [previewImage, setPreviewImage] = useState("");
    const [corporateLogo, setCorporateLogo] = useState(null);

    useEffect(() => {
        if (corporateLogoImage) {
            setCorporateLogo(`${base.imageUrl}${corporateLogoImage}`);
        }
    }, [corporateLogoImage]);

    const formik = useFormik({
        initialValues: {
            id: corporateUserData?.id || "",
            name: corporateUserData?.name || "",
            email: corporateUserData?.email || "",
            address: corporateUserData?.address?.address || "",
            longitude: corporateUserData?.address?.longitude || "",
            latitude: corporateUserData?.address?.latitude || "",
            phone_no: corporateUserData?.phone_no || "",
            ein_number: corporateUserData?.ein_number || "",
            tax_id_number: corporateUserData?.tax_id_number || "",
            registration_number: corporateUserData?.registration_number || "",
            work_permit_id: corporateUserData?.work_permit_id || "",
            about: corporateUserData?.about || "",
        },
        validationSchema: updateCorporateProfileSignUpSchema,
        enableReinitialize: true,
        onSubmit: async (values) => {
            const formData = new FormData();
            Object.entries(values).forEach(([key, value]) => {
                formData.append(key, value);
            });

            if (corporateLogo instanceof File) {
                formData.append("logo", corporateLogo);
            }

            const res = await dispatch(UpdateCorporateProfileAPI(formData));

            if (res.type === 'UpdateCorporateProfileAPI/fulfilled') {
                setIsEditing(false);
                if (res?.payload?.success || res?.payload?.status === 200) {
                    toast.success('Profile updated successfully!');
                    dispatch(updateUser(res.payload.data.data))
                } else {
                    errorHandler(res?.payload?.errors);
                }
            } else if (res.type === 'UpdateCorporateProfileAPI/rejected') {
                errorHandler(res?.payload?.errors);
                setIsEditing(false);
            } else {
                toast.error('Sorry, something went wrong!');
                setIsEditing(false);
            }
        }
    });

    const handleEditClick = () => {
        setIsEditing(!isEditing);
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setCorporateLogo(file);
            setPreviewImage(URL.createObjectURL(file));
        }
    };

    // GOOGLE MAP API INTIGRATION
    const inputRef = useRef(null);
    const [coordinates, setCoordinates] = useState({ lat: null, lng: null });
    const [getLocationAttributes, setGetLocationAttributes] = useState({})

    const { isLoaded } = useJsApiLoader({
        id: "google-map-script",
        googleMapsApiKey: process.env.REACT_APP_GOOGLEMAPS_API_KEY,
        libraries: ["places"],
    });

    const handlePlacesChange = () => {
        const places = inputRef.current.getPlaces();
        if (places && places?.length > 0) {
            const place = places[0];
            const lat = place.geometry.location.lat();
            const lng = place.geometry.location.lng();
            setCoordinates({ lat, lng });
            formik.setFieldValue('address', place.formatted_address);
            formik.setFieldValue('latitude', lat);
            formik.setFieldValue('longitude', lng);
            setGetLocationAttributes({
                address: place.formatted_address,
                longitude: lng,
                latitude: lat
            });
        }
    };
    // GOOGLE MAP API INTIGRATION

    return (
        <DashboardLayout>
            <div className="col-md-12 mt-4">
                <form onSubmit={formik.handleSubmit}>
                    <div className="profile-detail-box flex">
                        {/* Company Name */}
                        <div className="text-icon-show">
                            <div className="icon">
                                <i className="fa-regular fa-user"></i>
                            </div>
                            <div className="profile-text-box custom-input-box">
                                <p>Company Name</p>
                                {isEditing ? (
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="name"
                                        value={formik.values.name}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                ) : (
                                    <h3>{formik.values.name}</h3>
                                )}
                                {formik.touched.name && formik.errors.name && (
                                    <div className="text-danger">{formik.errors.name}</div>
                                )}
                            </div>
                        </div>

                        {/* Email */}
                        <div className="text-icon-show">
                            <div className="icon">
                                <i className="fa-solid fa-envelope"></i>
                            </div>
                            <div className="profile-text-box custom-input-box">
                                <p>Email Address</p>
                                <h3>{formik.values.email}</h3>
                            </div>
                        </div>

                        {/* Location */}
                        <div className="text-icon-show">
                            <div className="icon">
                                <i className="fa-solid fa-location-crosshairs"></i>
                            </div>
                            <div className="profile-text-box custom-input-box">
                                <p>Address</p>
                                {isEditing ? (
                                    isLoaded ? (
                                        <StandaloneSearchBox
                                            onLoad={(ref) => (inputRef.current = ref)}
                                            onPlacesChanged={handlePlacesChange}
                                        >
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="address"
                                                placeholder="Enter your address"
                                                value={formik.values.address}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                            />
                                        </StandaloneSearchBox>
                                    ) : (
                                        <p>Loading Google Maps...</p>
                                    )
                                ) : (
                                    <h3>{formik.values.address}</h3>
                                )}
                                {formik.touched.address && formik.errors.address && (
                                    <div className="text-danger">{formik.errors.address}</div>
                                )}
                            </div>
                        </div>

                        {/* Phone Number */}
                        <div className="text-icon-show">
                            <div className="icon">
                                <i className="fa-solid fa-phone-volume"></i>
                            </div>
                            <div className="profile-text-box custom-input-box">
                                <p>Phone Number</p>
                                {isEditing ? (
                                    <input
                                        type="number"
                                        className="form-control"
                                        name="phone_no"
                                        value={formik.values.phone_no}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                ) : (
                                    <h3>{formik.values.phone_no}</h3>
                                )}
                                {formik.touched.phone_no && formik.errors.phone_no && (
                                    <div className="text-danger">{formik.errors.phone_no}</div>
                                )}
                            </div>
                        </div>

                        {/* EIN Number */}
                        <div className="text-icon-show">
                            <div className="icon">
                                <i className="fa-solid fa-briefcase"></i>
                            </div>
                            <div className="profile-text-box custom-input-box">
                                <p>Company EIN Number</p>
                                {isEditing ? (
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="ein_number"
                                        value={formik.values.ein_number}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                ) : (
                                    <h3>{formik.values.ein_number}</h3>
                                )}
                                {formik.touched.ein_number && formik.errors.ein_number && (
                                    <div className="text-danger">{formik.errors.ein_number}</div>
                                )}
                            </div>
                        </div>

                        {/* Tax ID */}
                        <div className="text-icon-show">
                            <div className="icon">
                                <i className="fa-solid fa-shield-halved"></i>
                            </div>
                            <div className="profile-text-box custom-input-box">
                                <p>Tax ID Number</p>
                                {isEditing ? (
                                    <input
                                        type="number"
                                        name="tax_id_number"
                                        className="form-control"
                                        value={formik.values.tax_id_number}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                ) : (
                                    <h3>{formik.values.tax_id_number}</h3>
                                )}
                                {formik.touched.tax_id_number && formik.errors.tax_id_number && (
                                    <div className="text-danger">{formik.errors.tax_id_number}</div>
                                )}
                            </div>
                        </div>

                        {/* Registration Number */}
                        <div className="text-icon-show">
                            <div className="icon">
                                <i className="fa-solid fa-shield-halved"></i>
                            </div>
                            <div className="profile-text-box custom-input-box">
                                <p>Registration Number</p>
                                {isEditing ? (
                                    <input
                                        type="number"
                                        className="form-control"
                                        name="registration_number"
                                        value={formik.values.registration_number}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                ) : (
                                    <h3>{formik.values.registration_number}</h3>
                                )}
                                {formik.touched.registration_number && formik.errors.registration_number && (
                                    <div className="text-danger">{formik.errors.registration_number}</div>
                                )}
                            </div>
                        </div>

                        {/* Work Permit ID */}
                        <div className="text-icon-show">
                            <div className="icon">
                                <i className="fa-solid fa-address-book"></i>
                            </div>
                            <div className="profile-text-box custom-input-box">
                                <p>Work Permit ID</p>
                                {isEditing ? (
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="work_permit_id"
                                        value={formik.values.work_permit_id}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                ) : (
                                    <h3>{formik.values.work_permit_id}</h3>
                                )}
                                {formik.touched.work_permit_id && formik.errors.work_permit_id && (
                                    <div className="text-danger">{formik.errors.work_permit_id}</div>
                                )}
                            </div>
                        </div>

                        {/* About Company */}
                        <div className="text-icon-show">
                            <div className="icon">
                                <i className="fa-solid fa-address-card"></i>
                            </div>
                            <div className="profile-text-box custom-input-box">
                                <p>About Company</p>
                                {isEditing ? (
                                    <textarea
                                        className="form-control"
                                        name="about"
                                        value={formik.values.about}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                ) : (
                                    <h3>{formik.values.about}</h3>
                                )}
                                {formik.touched.about && formik.errors.about && (
                                    <div className="text-danger">{formik.errors.about}</div>
                                )}
                            </div>
                        </div>

                        <div className="profile-image-box">
                            <img src={previewImage || corporateLogo || 'default-image-url'} alt="Profile" />
                            <label htmlFor="upload-image" className="upload-image mt-3 d-flex justify-content-center">
                                <span className='bg-white'>Upload Image</span>
                            </label>
                            {isEditing && (
                                <input
                                    type="file"
                                    name="logo"
                                    id="upload-image"
                                    className="mt-4 bg-white d-none"
                                    accept="image/*"
                                    onChange={handleFileChange}
                                />
                            )}
                        </div>

                        {/* Buttons */}
                        <div className="d-flex justify-content-center mt-4">
                            {!isEditing ? (
                                <button
                                    type="button"
                                    className="btn btn-primary ps-5 pe-5"
                                    onClick={handleEditClick}
                                >
                                    Edit
                                </button>
                            ) : (
                                <button disabled={loading} type="button" onClick={formik.handleSubmit} className="btn btn-primary ps-5 pe-5">
                                    {!loading ? (
                                        "Submit"
                                    ) : (
                                        <i
                                            style={{ fontSize: "22px" }}
                                            className="fa-solid fa-spinner fa-spin"
                                        ></i>
                                    )}
                                </button>
                            )}
                        </div>
                    </div>
                </form>
            </div>
        </DashboardLayout>
    );
};

export default MyProfile;